import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X, PartyPopper } from "lucide-react";
import { useOnboardingStatus } from "../../hooks/useOnboarding";
import { ChecklistItems } from "./ChecklistItems";

/**
 * The getting-started checklist in a modal, opened on demand from the top-bar
 * Help menu. Unlike the dashboard card it ignores the "dismissed" flag — this
 * is the deliberate re-open path — and it stays available even after every step
 * is done (showing a done state).
 */
export function OnboardingChecklistModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Only fetch while the modal is open.
  const { data } = useOnboardingStatus(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const checklist = data?.checklist;
  const allDone = checklist ? checklist.completed === checklist.total : false;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("help.checklistModal.title")}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700/80"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800">
            <Sparkles size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-fg-primary">
              {t("help.checklistModal.title")}
            </h2>
            <p className="mt-0.5 text-sm text-fg-secondary">
              {t("help.checklistModal.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("help.checklistModal.close")}
            className="flex-none rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {checklist && (
          <>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                  style={{ width: `${checklist.percent}%` }}
                />
              </div>
              <span className="flex-none text-xs font-medium text-fg-secondary">
                {t("onboarding.checklist.progress", {
                  done: checklist.completed,
                  total: checklist.total,
                })}
              </span>
            </div>

            {allDone ? (
              <div className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800/40">
                <PartyPopper size={18} className="flex-none" />
                {t("help.checklistModal.allDone")}
              </div>
            ) : (
              <ChecklistItems
                checklist={checklist}
                onNavigate={onClose}
                className="mt-4 space-y-2"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
